import React, { useState } from "react";
import dayjs from "utils/dayjs";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { useFormik } from "formik";
import { Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { ConfirmDialog, Drawer, TextField, toast } from "@outpave/ds";
import CustomSkeleton from "components/common/CustomSkeleton";
import MerchantSearchAutoComplete, {
  Merchant,
} from "components/common/MerchantSearchAutoComplete";
import MerchantSearchType from "enums/MerchantSearchType";
import { UPDATE_RECEIPT } from "graphql/receipts";
import { editReceiptDetailsValidationSchema } from "validations/receiptLibrary";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { useColorScheme } from "context/ColorSchemeContext";
import { getGraphqlErrorMessage } from "utils/graphql";
import { getCurrencySymbol } from "utils/formatCash";
import { fullName } from "utils/fullName";
import { Receipt } from "utils/receipts/receiptFormat";
import UserAvatar from "components/common/users/UserAvatar";

/** Fields the drawer edits and hands back to the parent on a successful save. */
export type ReceiptEditPatch = Pick<
  Receipt,
  "companyName" | "amount" | "transactionDate"
>;

interface EditReceiptDrawerProps {
  open: boolean;
  /** The receipt being edited; the form seeds from its current values. */
  receipt: Receipt | null;
  /** A caller still fetching the receipt: shows the form skeleton until it lands. */
  loading?: boolean;
  /**
   * Show who uploaded the receipt. A surface where every receipt belongs to the
   * viewer has nothing to say here, which is a fact about the surface and not
   * about the record, so it cannot be read off `receipt.user`.
   */
  showUploader?: boolean;
  onClose: () => void;
  /**
   * Called after a successful update with the saved fields, so the parent can
   * merge them into its open receipt and refresh the library list.
   */
  onUpdated: (patch: ReceiptEditPatch) => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

interface EditReceiptFormValues {
  companyName: string;
  amount: number;
  /** Milliseconds since epoch, or null when no date is set. */
  transactionDate: number | null;
}

// The read-only rows: `label` is sm + medium, `body1` is md + regular, and the
// theme's MuiTypography root already paints text-primary, so only the label's
// secondary colour needs saying.
const FIELD_LABEL_SX = { color: "var(--color-text-secondary)" } as const;

/**
 * Side drawer for editing a receipt's core details (merchant, amount,
 * transaction date), shared by the admin receipt library and My Outpave.
 *
 * The caller owns the data: pass the receipt and, if you are still fetching it,
 * `loading`. On success the saved fields come back through `onUpdated` so an
 * open details view can merge them without refetching.
 */
const EditReceiptDrawer: React.FC<EditReceiptDrawerProps> = ({
  open,
  receipt,
  loading = false,
  showUploader = false,
  onClose,
  onUpdated,
  onAfterClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Drives the native date input's calendar-picker glyph so it follows the
  // app theme (otherwise WebKit renders it black in dark mode).
  const { colorScheme } = useColorScheme();
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [updateReceipt, { loading: updatingReceipt }] =
    useMutation(UPDATE_RECEIPT);

  const formik = useFormik<EditReceiptFormValues>({
    enableReinitialize: true,
    initialValues: {
      companyName: receipt?.companyName || "",
      amount: receipt?.amount ?? 0,
      transactionDate: receipt?.transactionDate || null,
    },
    validationSchema: editReceiptDetailsValidationSchema,
    onSubmit: async (values) => {
      if (!receipt) return;

      const parsedAmount =
        typeof values.amount === "number"
          ? values.amount
          : parseFloat(Number(values.amount).toFixed(2));

      try {
        await updateReceipt({
          variables: {
            id: receipt.id,
            input: {
              companyName: values.companyName,
              amount: parsedAmount,
              transactionDate: values.transactionDate,
            },
          },
        });
        toast.success(t("Receipt updated successfully"));
        onUpdated({
          companyName: values.companyName,
          amount: parsedAmount,
          // Guaranteed non-null by the validation schema (required date).
          transactionDate: values.transactionDate as number,
        });
        onClose();
      } catch (error) {
        const graphQLErrors =
          error && typeof error === "object" && "graphQLErrors" in error
            ? (error as { graphQLErrors: unknown }).graphQLErrors
            : undefined;
        const errorMessage = graphQLErrors
          ? getGraphqlErrorMessage(graphQLErrors)
          : t("Please try again!");
        toast.error(errorMessage);
      }
    },
  });

  const { handleFormikHelperText } = useFormikHelpers(formik);

  // Reset any in-progress edits once the drawer has slid out, so the fields hold still while it closes.
  const handleAfterClose = () => {
    formik.resetForm();
    setConfirmingLeave(false);
    onAfterClose?.();
  };

  const showSkeleton = open && loading && !receipt;

  // Every dismissal path runs this: the DS `loading` prop only swallows the
  // backdrop, so the guard against closing mid-save has to live here.
  const handleClose = () => {
    if (updatingReceipt) return;
    if (formik.dirty) {
      setConfirmingLeave(true);
      return;
    }
    onClose();
  };

  const dateValue = formik.values.transactionDate
    ? dayjs(formik.values.transactionDate).format("YYYY-MM-DD")
    : "";

  // Render the currency as its symbol (e.g. "$") rather than the raw code.
  const amountSymbol = receipt?.currency
    ? getCurrencySymbol(receipt.currency)
    : "";

  const uploaderName = fullName(receipt?.user);

  const handleSelectMerchant = (merchant: Merchant | null) => {
    formik.setFieldValue("companyName", merchant?.name || "");
    formik.setFieldTouched("companyName", true, false);
  };

  // Feed the current company name back into the autocomplete as its selected
  // value so it shows the saved merchant when the drawer opens.
  const currentMerchant: Merchant | null = formik.values.companyName
    ? {
        id: "",
        name: formik.values.companyName,
        logo: "",
        cachedLogo: "",
        url: "",
      }
    : null;

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        onAfterClose={handleAfterClose}
        title={t("Edit receipt")}
        // Layered over the full-screen Receipt Details Dialog, so lift it above
        // that modal's backdrop.
        sx={{ zIndex: theme.zIndex.modal + 2 }}
        loading={updatingReceipt}
        footer={
          <>
            <Button
              variant="default-outlined"
              onClick={handleClose}
              disabled={updatingReceipt}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={() => formik.handleSubmit()}
              disabled={
                showSkeleton ||
                updatingReceipt ||
                !formik.isValid ||
                !formik.dirty
              }
            >
              {t("Save")}
            </Button>
          </>
        }
      >
        {showSkeleton ? (
          <CustomSkeleton variant="form" rows={6} />
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Stack gap="var(--space-5)">
              <Typography
                variant="body2"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {t(
                  "Review and update these details so we can match this receipt to a transaction.",
                )}
              </Typography>

              {showUploader && receipt?.user ? (
                <Stack gap="var(--space-3)">
                  <Typography
                    variant="label"
                    component="span"
                    sx={FIELD_LABEL_SX}
                  >
                    {t("Posted By")}
                  </Typography>
                  <Inline gap="var(--space-3)">
                    <UserAvatar size="lg" user={receipt?.user} />
                    <Typography variant="body1" component="span">
                      {uploaderName}
                    </Typography>
                  </Inline>
                </Stack>
              ) : null}

              <MerchantSearchAutoComplete
                title={t("Merchant")}
                merchant={currentMerchant}
                onSelectMerchant={handleSelectMerchant}
                allowUseSearchValue
                required
                type={MerchantSearchType.brand}
                errorText={handleFormikHelperText("companyName") ?? ""}
                handleBlur={() => formik.setFieldTouched("companyName", true)}
              />

              <TextField
                label={t("Amount")}
                name="amount"
                type="number"
                fullWidth
                required
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  const rounded = parseFloat(Number(e.target.value).toFixed(2));
                  formik.setFieldValue(
                    "amount",
                    Number.isFinite(rounded) ? rounded : 0,
                  );
                  formik.handleBlur(e);
                }}
                inputProps={{ min: 0, step: 0.01 }}
                // Hide the native number spinner arrows.
                sx={{
                  "& input[type=number]": { MozAppearance: "textfield" },
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                }}
                leading={
                  amountSymbol ? (
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ color: "var(--color-text-tertiary)" }}
                    >
                      {amountSymbol}
                    </Typography>
                  ) : undefined
                }
                errorText={handleFormikHelperText("amount") ?? undefined}
              />

              <TextField
                label={t("Transaction date")}
                name="transactionDate"
                type="date"
                fullWidth
                required
                value={dateValue}
                onChange={(e) => {
                  const next = e.target.value
                    ? dayjs(e.target.value, "YYYY-MM-DD").valueOf()
                    : null;
                  formik.setFieldValue("transactionDate", next);
                  formik.setFieldTouched("transactionDate", true, false);
                }}
                onBlur={() => formik.setFieldTouched("transactionDate", true)}
                // Receipts can't be dated in the future.
                inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                // Match the native calendar-picker glyph to the app theme.
                sx={{ "& input": { colorScheme } }}
                errorText={
                  handleFormikHelperText("transactionDate") ?? undefined
                }
              />

              {receipt?.createdAt ? (
                <Stack gap="var(--space-3)">
                  <Typography
                    variant="label"
                    component="span"
                    sx={FIELD_LABEL_SX}
                  >
                    {t("Uploaded date")}
                  </Typography>
                  <Typography variant="body1" component="span">
                    {dayjs(receipt.createdAt).format("MMM D, YYYY, h:mm A")}
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          </form>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmingLeave}
        title={t("Unsaved Changes")}
        message={t("You have unsaved changes, are you sure you want to leave?")}
        confirmLabel={t("Leave")}
        cancelLabel={t("Cancel")}
        onCancel={() => setConfirmingLeave(false)}
        onConfirm={() => {
          setConfirmingLeave(false);
          onClose();
        }}
        // Above this drawer's own paper, which sits at modal + 2.
        sx={{ zIndex: theme.zIndex.modal + 3 }}
      />
    </>
  );
};

export default EditReceiptDrawer;
